/**
 * ProgressPhotos — Coach/client progress photo timeline with before/after comparison.
 * Photos stored in Supabase Storage bucket "progress-photos".
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, Modal, TouchableOpacity,
  Image, ScrollView, ActivityIndicator, Alert, Dimensions,
} from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { supabase } from '@/lib/supabase';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { X, Camera, ChevronLeft, ChevronRight, Columns2 } from 'lucide-react-native';

const BUCKET = 'progress-photos';
const { width: SCREEN_W } = Dimensions.get('window');

export interface ProgressPhoto {
  id: string;
  client_id: string;
  coach_id: string;
  url: string;
  pose: 'front' | 'side' | 'back';
  taken_at: string;
}

interface Props {
  visible: boolean;
  clientId: string;
  clientName: string;
  /** If true, show upload UI (client or coach can upload) */
  canUpload?: boolean;
  onClose: () => void;
}

export default function ProgressPhotos({ visible, clientId, clientName, canUpload = true, onClose }: Props) {
  const { theme } = useTheme();
  const c = theme.colors;

  const [photos, setPhotos] = useState<ProgressPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [compareLeft, setCompareLeft] = useState<ProgressPhoto | null>(null);
  const [compareRight, setCompareRight] = useState<ProgressPhoto | null>(null);

  const loadPhotos = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('progress_photos')
        .select('*')
        .eq('client_id', clientId)
        .order('taken_at', { ascending: false })
        .limit(100);

      if (!error && data) setPhotos(data as ProgressPhoto[]);
    } catch (err) {
      console.warn('[ProgressPhotos] load error', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (visible) loadPhotos();
  }, [visible, loadPhotos]);

  const uploadPhoto = async (pose: 'front' | 'side' | 'back') => {
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [3, 4],
      quality: 0.8,
    });
    if (result.canceled || !result.assets?.[0]) return;

    setUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const asset = result.assets[0];
      const id = Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
      const path = `${clientId}/${id}.jpg`;

      const response = await fetch(asset.uri);
      const blob = await response.blob();

      const { error: uploadErr } = await supabase.storage
        .from(BUCKET)
        .upload(path, blob, { upsert: false, contentType: asset.mimeType || 'image/jpeg' });

      if (uploadErr) throw uploadErr;

      const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(path);

      const photo: ProgressPhoto = {
        id,
        client_id: clientId,
        coach_id: user.id,
        url: urlData.publicUrl,
        pose,
        taken_at: new Date().toISOString(),
      };

      const { error: insertErr } = await supabase.from('progress_photos').insert(photo);
      if (insertErr) console.warn('[ProgressPhotos] insert error', insertErr.message);

      setPhotos((prev) => [photo, ...prev]);
    } catch (err: any) {
      Alert.alert('Upload Failed', err.message || 'Could not upload photo');
    } finally {
      setUploading(false);
    }
  };

  // Group photos by date
  const grouped = photos.reduce<Record<string, ProgressPhoto[]>>((acc, p) => {
    const dateKey = new Date(p.taken_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(p);
    return acc;
  }, {});
  const dateKeys = Object.keys(grouped);

  const handleCompareSelect = (photo: ProgressPhoto) => {
    if (!compareLeft) {
      setCompareLeft(photo);
    } else if (!compareRight) {
      setCompareRight(photo);
    } else {
      setCompareLeft(photo);
      setCompareRight(null);
    }
  };

  return (
    <Modal visible={visible} animationType="slide" transparent>
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: c.background, borderColor: c.border }]}>
          {/* Header */}
          <View style={styles.header}>
            <View>
              <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
                PROGRESS PHOTOS
              </Text>
              <Text style={{ color: c.textSecondary, fontSize: 12 }}>{clientName}</Text>
            </View>
            <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center' }}>
              <TouchableOpacity
                onPress={() => { setCompareMode(!compareMode); setCompareLeft(null); setCompareRight(null); }}
                accessibilityRole="button"
                accessibilityLabel={compareMode ? 'Exit compare mode' : 'Compare photos'}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}
              >
                <Columns2 size={20} color={compareMode ? c.accent : c.textSecondary} />
                <Text style={{ fontSize: 12, color: compareMode ? c.accent : c.textSecondary, fontWeight: '600' }}>Compare</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={onClose}>
                <X size={22} color={c.textSecondary} />
              </TouchableOpacity>
            </View>
          </View>

          {/* Compare mode */}
          {compareMode && (
            <View style={styles.compareContainer}>
              <Text style={{ color: c.textSecondary, fontSize: 12, fontWeight: '600', marginBottom: 8 }}>
                {!compareLeft ? 'Tap a photo for BEFORE' : !compareRight ? 'Tap a photo for AFTER' : 'Before / After'}
              </Text>
              <View style={styles.compareRow}>
                <View style={[styles.compareSlot, { backgroundColor: c.surface, borderColor: c.border }]}>
                  {compareLeft ? (
                    <Image source={{ uri: compareLeft.url }} style={styles.compareImg} resizeMode="cover" />
                  ) : (
                    <Text style={{ color: c.textTertiary, fontSize: 12 }}>Before</Text>
                  )}
                  {compareLeft && (
                    <Text style={[styles.compareDate, { color: c.textSecondary }]}>
                      {new Date(compareLeft.taken_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </Text>
                  )}
                </View>
                <View style={[styles.compareSlot, { backgroundColor: c.surface, borderColor: c.border }]}>
                  {compareRight ? (
                    <Image source={{ uri: compareRight.url }} style={styles.compareImg} resizeMode="cover" />
                  ) : (
                    <Text style={{ color: c.textTertiary, fontSize: 12 }}>After</Text>
                  )}
                  {compareRight && (
                    <Text style={[styles.compareDate, { color: c.textSecondary }]}>
                      {new Date(compareRight.taken_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </Text>
                  )}
                </View>
              </View>
            </View>
          )}

          {/* Upload buttons */}
          {canUpload && (
            <View style={styles.uploadRow}>
              {(['front', 'side', 'back'] as const).map((pose) => (
                <TouchableOpacity
                  key={pose}
                  style={[styles.uploadBtn, { backgroundColor: c.accent }]}
                  onPress={() => uploadPhoto(pose)}
                  disabled={uploading}
                >
                  <Camera size={14} color="#fff" />
                  <Text style={styles.uploadBtnText}>{pose.charAt(0).toUpperCase() + pose.slice(1)}</Text>
                </TouchableOpacity>
              ))}
              {uploading && <ActivityIndicator color={c.accent} style={{ marginLeft: 8 }} />}
            </View>
          )}

          {/* Photo timeline */}
          {loading ? (
            <ActivityIndicator color={c.accent} style={{ marginTop: 40 }} />
          ) : photos.length === 0 ? (
            <Text style={{ color: c.textTertiary, textAlign: 'center', marginTop: 40, fontSize: 14 }}>
              No progress photos yet.
            </Text>
          ) : (
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 20 }}>
              {dateKeys.map((dateKey) => (
                <View key={dateKey} style={{ marginBottom: 16 }}>
                  <Text style={[styles.dateHeader, { color: c.textSecondary }]}>{dateKey}</Text>
                  <View style={styles.photoGrid}>
                    {grouped[dateKey].map((photo) => (
                      <TouchableOpacity
                        key={photo.id}
                        style={[styles.photoThumb, { borderColor: (compareLeft?.id === photo.id || compareRight?.id === photo.id) ? c.accent : c.border }]}
                        onPress={() => compareMode && handleCompareSelect(photo)}
                        activeOpacity={compareMode ? 0.7 : 1}
                      >
                        <Image source={{ uri: photo.url }} style={styles.thumbImg} resizeMode="cover" />
                        <Text style={[styles.poseLabel, { color: c.textSecondary }]}>{photo.pose}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              ))}
            </ScrollView>
          )}
        </View>
      </View>
    </Modal>
  );
}

const THUMB_SIZE = (SCREEN_W - 80) / 3;

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.7)', justifyContent: 'flex-end' },
  modal: { borderTopLeftRadius: 2, borderTopRightRadius: 2, borderWidth: 1, borderBottomWidth: 0, maxHeight: '90%', minHeight: '60%', padding: 16 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  title: { fontSize: 16, fontWeight: '900', letterSpacing: 1 },
  uploadRow: { flexDirection: 'row', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' },
  uploadBtn: { flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 2 },
  uploadBtnText: { color: '#fff', fontSize: 12, fontWeight: '700' },
  dateHeader: { fontSize: 13, fontWeight: '800', letterSpacing: 0.5, marginBottom: 8 },
  photoGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  photoThumb: { width: THUMB_SIZE, height: THUMB_SIZE * 1.33, borderRadius: 2, borderWidth: 2, overflow: 'hidden' },
  thumbImg: { width: '100%', height: '100%' },
  poseLabel: { position: 'absolute', bottom: 2, left: 4, fontSize: 11, fontWeight: '700', textTransform: 'uppercase' },
  compareContainer: { marginBottom: 12 },
  compareRow: { flexDirection: 'row', gap: 8 },
  compareSlot: { flex: 1, aspectRatio: 0.75, borderRadius: 2, borderWidth: 1, alignItems: 'center', justifyContent: 'center', overflow: 'hidden' },
  compareImg: { width: '100%', height: '100%' },
  compareDate: { position: 'absolute', bottom: 4, fontSize: 11, fontWeight: '700' },
});
